import { t, tc } from '../i18n/i18n';
/**
 * Playing with two thumbs (2026-09-05).
 *
 * User: *"we need for phone adaptation and this important part. We need to
 * start from landing and HUB, then move to gameplay. Full mobile adaptation."*
 *
 * `input-controller.ts` opened with "No touch/mobile support — this is
 * explicitly out of scope for the MVP", and it was honest: the run bound
 * `keydown`/`keyup` and nothing else, while camera look was gated behind
 * Pointer Lock, which no phone browser implements. The hub was touch-correct
 * and so was the action bar, which made the gap worse rather than better — on
 * a phone you could fire an ability but you could not walk or turn.
 *
 * ## The shape
 *
 * One full-screen catcher under the HUD and above the canvas. Where a finger
 * lands decides what it does, which is what lets both thumbs work at once
 * without a single visible control in the middle of the screen:
 *
 * | Where | What |
 * |---|---|
 * | Lower left | The joystick. Direction while held; push past `SPRINT_AT` and you are sprinting |
 * | Anywhere else | Drag to look. Release without travelling far, and quickly, and it was a tap — which swings |
 * | The four plates | Attack, Block (hold), Jump, Use (hold) |
 *
 * **The stick is drawn, and it also floats.** The first version drew the ring
 * only under a finger already down, which is fine once you know it is there
 * and invisible when you do not — a phone player's first sight of the run was
 * a dungeon with no visible way to walk. So there is a base at
 * `HOME_INSET_PX`, always on screen, dim until touched: grab it and it stays
 * put, land anywhere else in the lower left and it comes to the thumb. A fixed
 * ring is a target you have to find while something is hitting you; a base you
 * can also ignore costs nothing and answers the first question a new player
 * has.
 *
 * **Tap-to-attack on the look area** is what makes it feel like a game rather
 * than a control panel: the same gesture that aims also swings, so the common
 * case — turn toward the thing, hit it — is one thumb and no travel. The
 * explicit Attack plate stays for discoverability and for a swing that must
 * not move the camera.
 *
 * ## Why it does not touch the keyboard path
 *
 * Nothing here replaces `InputController`; it feeds it. The stick writes
 * `touchDirection`, the plates call the same callbacks the keys do, and look
 * calls the camera's own `applyLookDelta` — the method Pointer Lock was
 * already using. A desktop with a mouse never mounts this at all, and the
 * gate is `(pointer: coarse)` rather than a width breakpoint: a 1024px tablet
 * is a touch device and a narrow desktop window is not, and what changes is
 * the *input*, not the layout.
 */

/** Pixels of finger travel that turn a tap into a drag. Below it, a release is a swing. */
const TAP_SLOP_PX = 12;
/** And how long a tap may take. Longer than this with no travel is a thumb resting, not a hit. */
const TAP_MS = 260;
/** Stick radius in CSS pixels — the distance at which the knob is at full tilt. */
const STICK_RADIUS_PX = 54;
/** Fraction of the radius past which the stick is also sprinting. */
const SPRINT_AT = 0.82;
/**
 * Push below this and the stick reads as centred.
 *
 * A thumb resting on glass is never still — it breathes, and the hand holding
 * the phone moves. Without a dead zone the delver drifts into a wall while
 * nobody is asking it to, which reads as the game being broken rather than as
 * two pixels of skin.
 */
const DEAD_ZONE_PX = 8;
/**
 * Where the stick sits when nobody is touching it, inset from the bottom-left
 * corner (its own radius plus a thumb's worth of edge).
 *
 * It has a home at all because the first version had none: the ring was drawn
 * only under a finger already down, so a phone player's first sight of the run
 * was a dungeon with no visible way to walk. An always-drawn base is the
 * difference between a control and a secret.
 */
const HOME_INSET_PX = 88;
/**
 * Land within this of the home centre and the stick stays put, using home as
 * its origin. Land further out in the move zone and the base jumps to the
 * thumb.
 *
 * Slightly wider than the drawn ring (60px) so aiming at the base and missing
 * by a thumb-width still grabs it rather than teleporting it a centimetre.
 */
const GRAB_RADIUS_PX = 92;
/**
 * Look sensitivity, in "mouse pixels per finger pixel".
 *
 * A finger crosses a phone in one motion where a mouse crosses a desk in
 * several, so a 1:1 mapping makes a phone feel sluggish and a tablet feel
 * wild. 1.35 is the number that let a thumb turn 180 degrees in one comfortable
 * swipe on a 390px screen without overshooting a target two metres away.
 */
const LOOK_GAIN = 1.35;

/** A plate's label size when the word fits — 13px on a 68px plate, 11px on the Target plate. */
const PLATE_FONT_PX = 13;
const TARGET_FONT_PX = 11;
/** The width a label may use inside the circle: the diameter less a margin, so the word clears the rim. */
const PLATE_TEXT_ROOM_PX = 58;
const TARGET_TEXT_ROOM_PX = 42;
/** Below this a label stops being readable at thumb distance; a translation that needs less must be shortened instead. */
const PLATE_FONT_MIN_PX = 9;
/**
 * Average advance of a bold Vollkorn plate label, in ems per character, as
 * laid out in Chrome over all seven languages (2026-10-16): 0.58 («Blocken»)
 * to 0.83 («Прыжок» — wide Cyrillic capitals). Deliberately the low end: the
 * estimate only picks a starting size, `fitPlateLabels` measures the wide
 * ones down once the plates are on screen, and a high estimate would shrink
 * every Latin word that already fit.
 */
const PLATE_EM_PER_CHAR = 0.6;

/**
 * Shrinks a plate's label until it fits the circle (QA 2026-10-16). Russian
 * «Использовать» at 13px ran across the gap onto «Прыжок» at 375px; German and
 * Estonian verbs are eight letters too. Pure and length-based so it is the
 * same on every device and testable without layout.
 */
export function plateFontPx(label: string, basePx: number = PLATE_FONT_PX, roomPx: number = PLATE_TEXT_ROOM_PX): number {
  const chars = [...label.trim()].length;
  if (chars === 0) return basePx;
  const fit = Math.floor(roomPx / (chars * PLATE_EM_PER_CHAR));
  return Math.max(PLATE_FONT_MIN_PX, Math.min(basePx, fit));
}

export interface TouchControlCallbacks {
  /** Right/down positive, in the same units `applyLookDelta` takes. */
  onLook: (dxPx: number, dyPx: number) => void;
  onAttack: () => void;
  onBlockChange: (active: boolean) => void;
  /** Space, for a thumb. Was Dodge until dodge went passive (2026-10-16). */
  onJump: () => void;
  onInteractStart: () => void;
  onInteractEnd: () => void;
  /**
   * Tab, for a thumb (2026-10-16). A phone had no way to pick a target at all,
   * and the refusal told it to "press Tab". Optional so a caller with nothing
   * to target simply gets no plate.
   */
  onCycleTarget?: () => void;
}

/**
 * Whether this device is driven by a thumb.
 *
 * `(pointer: coarse)` is the primary input's precision, which is the actual
 * question. `maxTouchPoints` is the fallback for browsers that do not report
 * the media query, and both are read defensively because this runs in tests
 * under jsdom, where neither exists.
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(pointer: coarse)')?.matches) return true;
  } catch {
    // Old browsers throw on an unknown media feature rather than returning false.
  }
  return (navigator?.maxTouchPoints ?? 0) > 0;
}

/** What one finger is currently doing. */
type Role = 'move' | 'look';

interface Track {
  role: Role;
  /**
   * What the stick is measured from — the home centre when the thumb grabbed
   * the drawn base, the touch point when it landed elsewhere. Kept per finger
   * rather than read back off the element so a relocation mid-drag (there is
   * none today) could never desynchronise the vector from the picture.
   */
  originX: number;
  originY: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  startedAt: number;
  travelled: number;
}

/**
 * Which half of the screen a touch belongs to.
 *
 * Exported and pure because it is the one decision that makes both thumbs work
 * at once, and it is much easier to get wrong than it looks: the move zone has
 * to be the lower *left* corner rather than the whole left half, or reaching
 * for the objective plaque at the top-left starts walking you into a wall.
 */
export function roleForTouch(
  x: number, y: number, width: number, height: number,
): Role {
  return x < width * 0.5 && y > height * 0.45 ? 'move' : 'look';
}

/** The stick's output for a finger that has travelled (dx, dy) from where it landed. */
export function stickVector(dx: number, dy: number): { x: number; z: number; sprint: boolean } {
  const distance = Math.hypot(dx, dy);
  if (distance < DEAD_ZONE_PX) return { x: 0, z: 0, sprint: false };
  const clamped = Math.min(distance, STICK_RADIUS_PX);
  // Normalised, so a half-pushed stick walks in the same direction a fully
  // pushed one runs in — the server takes a unit vector and a sprint flag, not
  // an analog speed, so pretending otherwise here would be a lie the movement
  // code cannot honour.
  return {
    x: dx / distance,
    // Screen-down is world-backward: dragging the stick toward you walks you
    // backward, which is the only mapping that survives someone thinking about
    // it for a second.
    z: -dy / distance,
    sprint: clamped / STICK_RADIUS_PX >= SPRINT_AT,
  };
}

export class TouchControls {
  private readonly root: HTMLDivElement;
  private readonly stick: HTMLDivElement;
  private readonly knob: HTMLDivElement;
  private readonly tracks = new Map<number, Track>();
  private cb: TouchControlCallbacks | null = null;
  private vector = { x: 0, z: 0 };
  private sprint = false;
  private blockHeld = false;
  /** The Target plate — see `buildPlates`. Revealed by `start` when the caller can cycle targets. */
  private targetPlate: HTMLButtonElement | null = null;
  private readonly plates: HTMLButtonElement[] = [];

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // Below every HUD panel (the action bar is z-1000, the run menu z-1400) so
    // a tap meant for a button reaches the button, and above the canvas so a
    // drag on the world reaches this. `touch-none` stops the browser treating
    // a drag as a scroll or a pinch before we ever see it.
    this.root.className = 'fixed inset-0 z-[820] touch-none select-none hidden';
    this.root.setAttribute('aria-hidden', 'true');

    this.stick = document.createElement('div');
    // Drawn from the first frame of the run, not conjured on touch — see
    // HOME_INSET_PX. Dimmed while idle so it reads as an affordance and not as
    // a thing currently happening.
    this.stick.className = 'absolute w-[7.5rem] h-[7.5rem] -ml-[3.75rem] -mt-[3.75rem] rounded-full '
      + 'border-2 border-[#e0b25f]/40 bg-[#0d0905]/35 pointer-events-none opacity-55 '
      + 'transition-[opacity,border-color] duration-150';
    this.stick.dataset.touch = 'stick';

    this.knob = document.createElement('div');
    this.knob.className = 'absolute left-1/2 top-1/2 w-14 h-14 -ml-7 -mt-7 rounded-full '
      + 'border border-[#f3d089]/70 bg-[#e0b25f]/30 shadow-[0_0_18px_rgba(224,178,95,0.35)]';
    this.stick.appendChild(this.knob);
    this.homeStick();
    this.root.appendChild(this.stick);
    this.root.appendChild(this.buildPlates());
    container.appendChild(this.root);
  }

  /**
   * The four verbs that are not "walk" or "look".
   *
   * Text, not glyphs: the HUD glyph set has no shield and no hand, and a
   * borrowed icon that means something else is worse at thumb size than a
   * word, where there is room for one.
   */
  private buildPlates(): HTMLDivElement {
    const cluster = document.createElement('div');
    // Below the action bar, not above it: `app.css` raises the bar to 9.5rem on
    // a touch device precisely to clear this band, and a cluster at 10.5rem
    // would sit behind the very buttons that move made room for. Measured, not
    // guessed — the first pass put ATTACK and USE under the ability slots.
    cluster.className = 'absolute right-[max(0.75rem,env(safe-area-inset-right))] '
      + 'bottom-[max(0.85rem,env(safe-area-inset-bottom))] '
      + 'grid grid-cols-2 gap-2 pointer-events-auto';

    const plate = (label: string, hold: boolean, on: () => void, off?: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'w-[4.25rem] h-[4.25rem] rounded-full border border-[#57462f] '
        + 'bg-[radial-gradient(circle_at_50%_26%,#5b4a37,#2b2219_74%)] '
        + 'shadow-[inset_0_1px_0_rgba(255,222,176,0.10),0_6px_14px_rgba(0,0,0,0.5)] '
        + 'px-0 leading-none whitespace-nowrap overflow-hidden '
        + 'font-heading font-bold text-[13px] text-[#ffeccc] active:border-gold active:bg-[#6b563f]';
      b.textContent = label;
      b.style.fontSize = `${plateFontPx(label, PLATE_FONT_PX)}px`;
      this.plates.push(b);
      // `pointerdown`, never `click`: a click on a touch screen arrives after
      // the browser has waited to see whether a second tap is coming, and a
      // swing that lands 300ms after the thumb is a swing at where the enemy
      // used to be.
      b.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); on(); });
      if (hold) {
        const release = (e: Event): void => { e.preventDefault(); off?.(); };
        b.addEventListener('pointerup', release);
        b.addEventListener('pointercancel', release);
        b.addEventListener('pointerleave', release);
      }
      return b;
    };

    cluster.append(
      plate(tc('touch control', 'Block'), true, () => this.setBlock(true), () => this.setBlock(false)),
      plate(t('Attack'), false, () => this.cb?.onAttack()),
      plate(tc('touch control', 'Use'), true, () => this.cb?.onInteractStart(), () => this.cb?.onInteractEnd()),
      plate(tc('touch control', 'Jump'), false, () => this.cb?.onJump()),
    );
    // Target (2026-10-16) — Tab for a thumb. Hung off the cluster's left edge
    // beside its top row rather than added as a fifth plate: a third row would
    // climb into the band `app.css` keeps clear for the action bar. Smaller
    // than the combat plates (it is pressed between fights, not in them) but
    // still over the 44px floor. Shown only once a caller wires it.
    const target = plate(tc('touch control', 'Target'), false, () => this.cb?.onCycleTarget?.());
    target.dataset.touch = 'target';
    target.classList.remove('w-[4.25rem]', 'h-[4.25rem]', 'text-[13px]');
    target.classList.add('absolute', 'right-[calc(100%+0.5rem)]', 'top-[0.6rem]', 'w-[3.1rem]', 'h-[3.1rem]', 'hidden');
    target.style.fontSize = `${plateFontPx(target.textContent ?? '', TARGET_FONT_PX, TARGET_TEXT_ROOM_PX)}px`;
    this.targetPlate = target;
    cluster.appendChild(target);
    return cluster;
  }

  /**
   * The measured half of the fit (2026-10-16). `plateFontPx` sizes from the
   * letter count before there is any layout; once the cluster is on screen the
   * real width is known, and a word the estimate under-called — the DOM's bold
   * Vollkorn runs wider than a canvas measure of it, «Прыжок» by a quarter —
   * steps down until it clears the rim. Once per run start, so no per-frame reads.
   */
  private fitPlateLabels(): void {
    for (const b of this.plates) {
      if (b.classList.contains('hidden')) continue;
      const room = b.clientWidth - 10;
      if (room <= 0) continue; // not laid out (jsdom, or hidden)
      const range = document.createRange();
      range.selectNodeContents(b);
      let px = parseFloat(b.style.fontSize) || PLATE_FONT_PX;
      while (px > PLATE_FONT_MIN_PX && range.getBoundingClientRect().width > room) {
        px -= 0.5;
        b.style.fontSize = `${px}px`;
      }
    }
  }

  private setBlock(active: boolean): void {
    if (this.blockHeld === active) return;
    this.blockHeld = active;
    this.cb?.onBlockChange(active);
  }

  start(cb: TouchControlCallbacks): void {
    this.cb = cb;
    this.targetPlate?.classList.toggle('hidden', !cb.onCycleTarget);
    this.root.classList.remove('hidden');
    document.documentElement.classList.add('touch-play');
    this.fitPlateLabels();
    this.root.addEventListener('pointerdown', this.onDown);
    this.root.addEventListener('pointermove', this.onMove);
    this.root.addEventListener('pointerup', this.onUp);
    this.root.addEventListener('pointercancel', this.onUp);
  }

  /** Everything the thumbs were holding, forgotten — the run menu's `releaseAll`, for touch. */
  releaseAll(): void {
    this.tracks.clear();
    this.vector = { x: 0, z: 0 };
    this.sprint = false;
    this.setBlock(false);
    this.homeStick();
  }

  dispose(): void {
    this.root.removeEventListener('pointerdown', this.onDown);
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerup', this.onUp);
    this.root.removeEventListener('pointercancel', this.onUp);
    document.documentElement.classList.remove('touch-play');
    this.root.remove();
    this.cb = null;
  }

  get direction(): { x: number; z: number } {
    return this.vector;
  }

  get sprintHeld(): boolean {
    return this.sprint;
  }

  private readonly onDown = (e: PointerEvent): void => {
    const rect = this.root.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const role = roleForTouch(localX, localY, rect.width, rect.height);
    // One finger per role. A second thumb in the move zone while the first is
    // still down would otherwise fight it for the same vector.
    for (const track of this.tracks.values()) if (track.role === role) return;

    // Grab the drawn base, or take the stick with you. Both are the same code
    // path from here down; only where it is measured from differs.
    let originX = e.clientX;
    let originY = e.clientY;
    if (role === 'move') {
      const homeX = HOME_INSET_PX;
      const homeY = rect.height - HOME_INSET_PX;
      const onBase = Math.hypot(localX - homeX, localY - homeY) <= GRAB_RADIUS_PX;
      if (onBase) {
        originX = rect.left + homeX;
        originY = rect.top + homeY;
        this.wakeStick();
      } else {
        this.moveStickTo(localX, localY);
      }
    }

    this.tracks.set(e.pointerId, {
      role,
      originX,
      originY,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      startedAt: e.timeStamp,
      travelled: 0,
    });
    this.root.setPointerCapture?.(e.pointerId);
    if (role === 'move') this.moveKnob(e.clientX - originX, e.clientY - originY);
  };

  private readonly onMove = (e: PointerEvent): void => {
    const track = this.tracks.get(e.pointerId);
    if (!track) return;
    const dx = e.clientX - track.lastX;
    const dy = e.clientY - track.lastY;
    track.lastX = e.clientX;
    track.lastY = e.clientY;
    track.travelled += Math.hypot(dx, dy);
    if (track.role === 'look') {
      this.cb?.onLook(dx * LOOK_GAIN, dy * LOOK_GAIN);
      return;
    }
    const totalX = e.clientX - track.originX;
    const totalY = e.clientY - track.originY;
    const out = stickVector(totalX, totalY);
    this.vector = { x: out.x, z: out.z };
    if (out.sprint !== this.sprint) {
      this.sprint = out.sprint;
      // The ring itself is the sprint indicator: at full tilt it goes gold.
      // There is no stamina bar in a thumb's field of view, and a player who
      // cannot tell walking from running cannot ration either.
      this.stick.classList.toggle('border-[#e0b25f]/40', !out.sprint);
      this.stick.classList.toggle('border-[#ffd479]', out.sprint);
    }
    this.moveKnob(totalX, totalY);
  };

  private readonly onUp = (e: PointerEvent): void => {
    const track = this.tracks.get(e.pointerId);
    if (!track) return;
    this.tracks.delete(e.pointerId);
    if (track.role === 'move') {
      this.vector = { x: 0, z: 0 };
      this.sprint = false;
      this.homeStick();
      return;
    }
    // A look that went nowhere, quickly, was a tap: swing.
    const quick = e.timeStamp - track.startedAt <= TAP_MS;
    if (quick && track.travelled <= TAP_SLOP_PX) this.cb?.onAttack();
  };

  /** Put the base back where it lives, dim, centred, and not sprinting. */
  private homeStick(): void {
    this.stick.style.left = `${HOME_INSET_PX}px`;
    // `calc` rather than a measured pixel, so the base is correct on the first
    // frame — before the layer has a box — and stays correct through a rotate
    // without anything having to listen for one.
    this.stick.style.top = `calc(100% - ${HOME_INSET_PX}px)`;
    this.stick.style.opacity = '';
    this.stick.classList.add('border-[#e0b25f]/40');
    this.stick.classList.remove('border-[#ffd479]');
    this.moveKnob(0, 0);
  }

  /** The base is where the thumb already is; brighten it and keep it there. */
  private wakeStick(): void {
    this.stick.style.opacity = '1';
  }

  private moveStickTo(x: number, y: number): void {
    this.stick.style.left = `${x}px`;
    this.stick.style.top = `${y}px`;
    this.wakeStick();
  }

  private moveKnob(dx: number, dy: number): void {
    const distance = Math.hypot(dx, dy);
    const scale = distance > STICK_RADIUS_PX ? STICK_RADIUS_PX / distance : 1;
    this.knob.style.transform = `translate(${dx * scale}px, ${dy * scale}px)`;
  }
}
